import { observer } from 'mobx-react-lite';
import * as React from 'react';
import type AllowedResizeTypes from '../../store/schema/AllowedResizeTypes';
import canResizeItem from '../../calculation/canResizeItem';
import Draggable from 'owa-dnd/lib/components/Draggable';
import ensureDragTipMounted from './ensureDragTipMounted';
import SurfaceResizeBox from './SurfaceResizeBox';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import {
    SurfaceDragDropConfig,
    SurfaceItem,
    DragOperationType,
    CalendarSurfaceStore,
    lockSelectedItem,
} from 'owa-calendar-surface-dategrid';
import { endDragOperation } from '../../actions/endDragOperation';
import { initializeDragOperation } from '../../actions/initializeDragOperation';
import { isDragging } from '../../actions/isDragging';
import { setDragStartPosition } from '../../actions/setDragStartPosition';
import { getIsTouchOnlyDevice } from 'owa-layout';

let styles = require('./SurfaceDraggable.scss');
let classNames = require('classnames/bind').bind(styles);

/**
 * Props for the surface draggable component
 */
export interface SurfaceDraggableProps {
    item: SurfaceItem;
    store: CalendarSurfaceStore;
    dragDropConfig: SurfaceDragDropConfig;
    dragType: DragOperationType;
    allowedResizeTypes?: AllowedResizeTypes;
    className?: any;
    children?: React.ReactNode;
}

/**
 * Surface draggable behavior. Attach to calendar items on the surface to make them draggable.
 */
export const SurfaceDraggable = observer(function SurfaceDraggable(props: SurfaceDraggableProps) {
    const canDrag = (): boolean => {
        let { item, dragDropConfig } = props;
        return !getIsTouchOnlyDevice() && dragDropConfig.canDrag(item.model);
    };
    const getDragData = (): DragData => {
        return {
            // this is a temporary solution untill I refactor the Client-Controls Droppable and Draggle components to handle containerId in DragData
            // tracked by VSO #33820
            itemType: `${DraggableItemTypes.CalendarItem}_${props.item.model.ParentFolderId.Id}`,
        };
    };
    const onMouseDown = (ev: MouseEvent) => {
        if (canDrag()) {
            setDragStartPosition(props.store, ev.pageX, ev.pageY);
            lockSelectedItem(props.store);
        }
        // This is needed to prevent the grid from getting the clicks to
        // the draggable items
        ev.stopPropagation();
    };
    const onDragStart = (dragInfo: DragData, pageX: number, pageY: number) => {
        let { store, item, dragType } = props;
        initializeDragOperation(store, item.model, dragType);
        ensureDragTipMounted(store);
    };
    const onDragEnd = (dragInfo: DragData) => {
        let { store, dragDropConfig } = props;
        endDragOperation(store, dragDropConfig);
    };
    let { item, store, className, dragDropConfig, dragType, allowedResizeTypes } = props;
    let { dragDropState } = store;
    let dragging =
        isDragging(dragDropState) &&
        dragDropState.draggedItemModel.ItemId.Id == item.model.ItemId.Id;
    let classes = classNames({ draggedItem: dragging }, className || '');
    let canResize =
        !getIsTouchOnlyDevice() &&
        canResizeItem(store, dragDropConfig, item.model) &&
        dragType == DragOperationType.Drag;
    return (
        <Draggable
            canDrag={canDrag}
            classNames={classes}
            getDragData={getDragData}
            getDragPreview={getDragPreview}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onMouseDown={onMouseDown}
            showDefaultNoDragImage={true}>
            {!canResize && props.children}
            {canResize && (
                <SurfaceResizeBox
                    store={store}
                    dragDropConfig={dragDropConfig}
                    item={item}
                    allowedResizeTypes={allowedResizeTypes}
                    colorScheme={dragDropConfig.colorProvider(item.model)}>
                    {props.children}
                </SurfaceResizeBox>
            )}
        </Draggable>
    );
});

function getDragPreview(dragData: DragData) {
    let dragPreview = document.createElement('div');
    dragPreview.className = styles.dragHelper;
    return dragPreview;
}
